import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import FavoriteButton from "@/components/base/FavoriteButton";
import Lightbox from "@/components/base/Lightbox";
import MovieCard from "@/components/base/MovieCard";
import NotFoundBlock from "@/components/base/NotFoundBlock";
import ShareModal from "@/components/base/ShareModal";
import Toast from "@/components/base/Toast";
import useFavorites from "@/hooks/useFavorites";
import usePageMeta from "@/hooks/usePageMeta";
import { movieItems, movieStills } from "@/mocks/content";
import { formatDate, formatViews } from "@/lib/format";

export default function MovieDetailPage() {
  const { id } = useParams();
  const movie = movieItems.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [toast, setToast] = useState("");

  usePageMeta(
    movie ? `${movie.title} - 影视推荐 - 红桃网` : "内容不存在 - 红桃网",
    movie
      ? `${movie.title}（${movie.year}，${movie.region}）：${movie.synopsis.slice(0, 60)} 红桃网影视推荐。`
      : "你访问的影视作品不存在，请返回影视推荐频道继续浏览。",
    movie ? `${movie.title},${movie.genres.join(",")},影视推荐,红桃网` : "影视推荐,红桃网",
  );

  const related = useMemo(() => {
    if (!movie) return [];
    const sameGenre = movieItems.filter(
      (item) => item.id !== movie.id && item.genres.some((genre) => movie.genres.includes(genre)),
    );
    const others = movieItems.filter(
      (item) => item.id !== movie.id && !item.genres.some((genre) => movie.genres.includes(genre)),
    );
    return [...sameGenre, ...others].slice(0, 3);
  }, [movie]);

  if (!movie) {
    return <NotFoundBlock title="作品不存在" backTo="/recommendations" backLabel="返回影视推荐" />;
  }

  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    toggleFavorite(movie.id);
    setToast(favorited ? "已取消收藏（本地演示）" : "已加入收藏（仅保存在本机浏览器）");
  };

  return (
    <>
      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src={movie.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-30 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/90 to-foreground-950/50" />
        <div className="relative mx-auto w-full max-w-portal px-4 py-6 md:px-6 md:py-9">
          <Breadcrumbs
            tone="light"
            items={[
              { label: "首页", to: "/" },
              { label: "影视推荐", to: "/recommendations" },
              { label: movie.title },
            ]}
          />

          <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:gap-7">
            <div className="w-32 shrink-0 overflow-hidden rounded-lg border border-background-50/15 sm:w-44 md:w-52">
              <div className="aspect-[3/4] w-full bg-foreground-900">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - 红桃网影视推荐`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-sm bg-primary-500 px-2 py-1 text-[11px] text-background-50">{movie.media}</span>
                <span className="rounded-sm border border-background-50/20 px-2 py-1 text-[11px] text-background-100/80">
                  {movie.region}
                </span>
                <span className="rounded-sm border border-background-50/20 px-2 py-1 text-[11px] text-background-100/80">
                  {movie.year}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-background-100/60">
                  <i className="ri-eye-line" />
                  {formatViews(movie.views)} 次浏览
                </span>
              </div>

              <h1 className="mt-2.5 font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
                {movie.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-end gap-4">
                <div className="flex items-end gap-1">
                  <span className="font-label text-3xl font-extrabold leading-none text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="pb-0.5 font-label text-xs text-background-100/50">/ 10</span>
                </div>
                <span className="h-8 w-px bg-background-50/20" />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-widest text-background-100/50">推荐指数</p>
                  <p className="mt-1 font-label text-sm font-semibold text-background-50">{movie.recommendIndex} / 100</p>
                </div>
                <span className="h-8 w-px bg-background-50/20" />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-widest text-background-100/50">上映信息</p>
                  <p className="mt-1 text-sm text-background-100/85">{formatDate(movie.publishedAt)}</p>
                </div>
              </div>

              <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-100/70">
                <span>导演 {movie.director}</span>
                <span className="hidden sm:inline text-background-100/30">|</span>
                <span className="truncate">主演 {movie.cast.join(" / ")}</span>
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <FavoriteButton active={favorited} onToggle={handleFavorite} />
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 px-4 py-2 text-sm text-background-100 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <Link
                  to="/recommendations"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 px-4 py-2 text-sm text-background-100 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
                >
                  <i className="ri-arrow-left-line" />
                  返回片单
                </Link>
              </div>
              <p className="mt-2 text-[11px] text-background-100/40">收藏仅保存在本机浏览器，不会上传到服务器。</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-portal px-4 py-7 md:px-6 md:py-9">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <div className="min-w-0">
            <section>
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-primary-500" />
                剧情简介
              </h2>
              <p className="mt-4 text-[15px] leading-[1.95] text-foreground-800">{movie.synopsis}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {movie.genres.map((genre) => (
                  <span key={genre} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                    {genre}
                  </span>
                ))}
                {movie.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-background-200 px-3 py-1 text-xs text-foreground-500">
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            <section className="mt-9">
              <div className="flex items-end justify-between gap-4 border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  剧照墙
                </h2>
                <span className="text-xs text-foreground-400">点击图片可放大查看</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {movieStills.map((still, index) => (
                  <button
                    key={still}
                    type="button"
                    onClick={() => setLightboxIndex(index)}
                    aria-label={`查看第 ${index + 1} 张剧照`}
                    className="group relative aspect-[3/2] cursor-pointer overflow-hidden rounded-md border border-background-200 bg-background-200"
                  >
                    <img
                      src={still}
                      alt={`${movie.title} 剧照 ${index + 1}`}
                      title={`${movie.title} 剧照 ${index + 1}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 text-background-50 opacity-0 transition-all duration-300 group-hover:bg-foreground-950/35 group-hover:opacity-100">
                      <i className="ri-zoom-in-line text-xl" />
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section className="mt-9">
              <div className="mb-4 flex items-end justify-between gap-4 border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  相关推荐
                </h2>
                <Link to="/recommendations" className="flex items-center gap-0.5 text-xs text-foreground-500 hover:text-primary-600">
                  更多作品
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} />
                ))}
              </div>
            </section>

            <div className="mt-8 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500" />
                本页作品资料、评分与剧照均为虚构演示内容，仅用于展示页面效果。
              </p>
              <Link
                to="/recommendations"
                className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回影视推荐
              </Link>
            </div>
          </div>

          <aside className="space-y-4 self-start lg:sticky lg:top-[116px]">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-file-list-3-line text-primary-500" />
                作品资料
              </h2>
              <dl className="mt-3 space-y-2.5 text-[13px]">
                {[
                  { label: "作品类型", value: movie.media },
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.cast.join(" / ") },
                  { label: "上映年份", value: `${movie.year} 年` },
                  { label: "地区", value: movie.region },
                  { label: "类型", value: movie.genres.join(" / ") },
                  { label: "评分", value: `${movie.rating.toFixed(1)} 分` },
                  { label: "推荐指数", value: `${movie.recommendIndex} / 100` },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  热门影视
                </h2>
                <span className="font-label text-[10px] tracking-widest text-foreground-400">排行</span>
              </div>
              <ol className="divide-y divide-background-100 px-4">
                {[...movieItems]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 5)
                  .map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/movies/${item.id}`} className="group flex items-center gap-3 py-3">
                        <span
                          className={[
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm font-label text-[11px] font-bold",
                            index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500",
                          ].join(" ")}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[13px] text-foreground-800 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="flex shrink-0 items-center gap-1 font-label text-[11px] text-accent-700">
                          <i className="ri-star-fill text-[10px]" />
                          {item.rating.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${movie.title}（${movie.year}）`} />
      <Lightbox
        images={movieStills}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onChange={setLightboxIndex}
        alt={`${movie.title} 剧照`}
      />
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </>
  );
}